import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth APIs - Updated for TenantID + EmployeeID
export const authAPI = {
  login: (data) => api.post("/auth/login", data), // {tenant_id, employee_id, password}
  getMe: () => api.get("/auth/me"),
};

// Tenant APIs
export const tenantAPI = {
  create: (data) => api.post("/tenants", data),
  getAll: () => api.get("/tenants"),
  getById: (tenantId) => api.get(`/tenants/${tenantId}`),
  update: (tenantId, data) => api.put(`/tenants/${tenantId}`, data),
  suspend: (tenantId) => api.post(`/tenants/${tenantId}/suspend`),
  activate: (tenantId) => api.post(`/tenants/${tenantId}/activate`),
};

// Subscription Plan APIs
export const subscriptionPlanAPI = {
  getAll: () => api.get("/subscription-plans"),
  create: (data) => api.post("/subscription-plans", data),
  update: (id, data) => api.put(`/subscription-plans/${id}`, data),
  delete: (id) => api.delete(`/subscription-plans/${id}`),
};

// User APIs - Updated
export const userAPI = {
  create: (data) => api.post("/users", data),
  getAll: (params) => api.get("/users", { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { new_password: newPassword }),
  activate: (id) => api.post(`/users/${id}/activate`),
  deactivate: (id) => api.post(`/users/${id}/deactivate`),
  markResigned: (id, lastWorkingDate) => api.post(`/users/${id}/mark-resigned?last_working_date=${lastWorkingDate}`),
  getManagersByDepartment: (departmentId) => api.get(`/users/managers/${departmentId}`),
};

// Department APIs
export const departmentAPI = {
  create: (data) => api.post("/departments", data),
  getAll: (params) => api.get("/departments", { params }),
  update: (id, data) => api.put(`/departments/${id}`, data),
};

// Designation APIs
export const designationAPI = {
  create: (data) => api.post("/designations", data),
  getAll: (params) => api.get("/designations", { params }),
};

// Location APIs
export const locationAPI = {
  create: (data) => api.post("/locations", data),
  getAll: (params) => api.get("/locations", { params }),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

// Attendance APIs - Updated
export const attendanceAPI = {
  checkIn: (data) => api.post("/attendance/check-in", data),
  checkOut: (data) => api.post("/attendance/check-out", data),
  getToday: () => api.get("/attendance/today"),
  getAll: (params) => api.get("/attendance", { params }),
  export: (params) => api.get("/attendance/export", { params, responseType: "blob" }),
  getMissing: () => api.get("/attendance/missing"),
  getCalendar: (params) => api.get("/attendance/calendar", { params }),
  updateCalendar: (data) => api.put("/attendance/calendar", data),
};

// Holiday / Calendar APIs
export const holidayAPI = {
  getAll: (params) => api.get("/holidays", { params }),
  create: (data) => api.post("/holidays", data),
  upload: (formData, tenantId) =>
    api.post(`/holidays/upload${tenantId ? `?tenant_id=${tenantId}` : ""}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  delete: (id) => api.delete(`/holidays/${id}`),
};

// Comp-off APIs
export const compOffAPI = {
  getAll: (params) => api.get("/comp-offs", { params }),
  avail: (id, data) => api.post(`/comp-offs/${id}/avail`, data),
};

// Leave APIs - Updated
export const leaveAPI = {
  createType: (data) => api.post("/leave-types", data),
  getTypes: (params) => api.get("/leave-types", { params }),
  apply: (data) => api.post("/leaves/apply", data),
  getAll: (params) => api.get("/leaves", { params }),
  action: (id, data) => api.post(`/leaves/${id}/action`, data),
  getBalance: () => api.get("/leaves/balance"),
  export: (params) => api.get("/leaves/export", { params, responseType: "blob" }),
};

// Salary APIs
export const salaryAPI = {
  createStructure: (data) => api.post("/salary-structures", data),
  getStructures: (params) => api.get("/salary-structures", { params }),
  generateSlips: (month, tenantId) => api.post(`/salary-slips/generate?month=${month}${tenantId ? `&tenant_id=${tenantId}` : ""}`),
  getSlips: (params) => api.get("/salary-slips", { params }),
  approveSlip: (id) => api.post(`/salary-slips/${id}/approve`),
  publishSlip: (id) => api.post(`/salary-slips/${id}/publish`),
};

// Notice APIs
export const noticeAPI = {
  create: (data) => api.post("/notices", data),
  getAll: (params) => api.get("/notices", { params }),
  getUnreadCount: () => api.get("/notices/unread-count"),
  acknowledge: (id) => api.post(`/notices/${id}/acknowledge`),
};

// Resignation APIs - Updated
export const resignationAPI = {
  submit: (data) => api.post("/resignations", data),
  getAll: (params) => api.get("/resignations", { params }),
  action: (id, action, remarks) => api.post(`/resignations/${id}/action?action=${action}${remarks ? `&remarks=${encodeURIComponent(remarks)}` : ""}`),
  withdraw: (id) => api.post(`/resignations/${id}/withdraw`),
  complete: (id) => api.post(`/resignations/${id}/complete`),
};

// Document APIs
export const documentAPI = {
  upload: (formData) =>
    api.post("/documents", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  getAll: (params) => api.get("/documents", { params }),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: "blob" }),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Notification APIs
export const notificationAPI = {
  getAll: (params) => api.get("/notifications", { params }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post("/notifications/read-all"),
  getUnreadCount: () => api.get("/notifications/unread-count"),
};

// Audit Log APIs
export const auditLogAPI = {
  getAll: (params) => api.get("/audit-logs", { params }),
  export: (params) => api.get("/audit-logs/export", { params, responseType: "blob" }),
};

// Dashboard APIs
export const dashboardAPI = {
  getStats: () => api.get("/dashboard/stats"),
};

export default api;
